/* array.control_adapter.js */

"use strict";

const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const q = require("q");
const CheckBox = require("@gluenetworks/input_checkbox_control");
const InputTextControl = require("@gluenetworks/input_text_control");
const InputNumberControl = require("@gluenetworks/input_number_control");
const {v4: Uuid} = require("uuid");
const StyleUtil = require("../StyleUtil");

/**
 * @event ArrayControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class ArrayControlAdapter
 * @classdesc renders the Select Options Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires ArrayControlAdapter#ValueChanged
 */
module.exports = function ArrayAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    var loadedDeferred = q.defer();
    var addedItemPromises = [];
    //call Event Emitter Constructor
    EventEmitter.call(this);
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    var _scope;
    var _oldValue;
    let _oldInvalidStatus;
    let _oldInProgressStatus = false;
    var _typeConfigSettings;
    var _items = [];
    var _itemsRegistry = {};
    var _allowAdd = true;
    let _allowClone = false;
    var _allowDelete = true;
    var _allowReOrder = true;
    var _allowEdit = true;
    var _isCheckboxList = false;
    var _allowCheckAll = false;
    var _allowUncheckAll = false;
    var _displayLineCount;
    var _labels;
    var _defaultSimpleValue = "";

    // If the array in not in a complete all type then check the accordion setting.
    // If the array IS in a @complete all type then the accordion setting should be placed on the @complete type.
    if (this.name !== "@Complete") {
        this._accordionDisplay = _.get(formCtrlDef, "configSettings.Display.accordion", false);
    }

    if (_.has(formCtrlDef, "configSettings.value[0].fieldType")) {
        if (
            formCtrlDef.configSettings.value[0].fieldType === "String" ||
            formCtrlDef.configSettings.value[0].fieldType === "Number"
        ) {
            _defaultSimpleValue = formCtrlDef.configSettings.value[0].value;
        }
    }

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/

    /**
     * @typedef ArrayItem
     * @property id {string} The unique id to reference this ArrayItem
     * @property renderer {object}
     * @property renderer.api {RendererApi} The renderer API object for this option
     * @property renderer.settings {RendererSettings} The renderer Settings object for this option
     * @property renderer.elem {DomElement} The Element that represents this option's renderer
     */
    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    /**
     * Fire the Value Changed Event
     * @param silent {Array} If true will not emit the ValueChanged event.
     * @private
     */
    function _fireValueChanged(silent) {
        //Get the new Value
        var newVal = self.getValue();
        if (!silent) {
            self.emit("ValueChanged", {
                name: formCtrlDef.name,
                containingElem: self.arrayParentElem,
                controlAdapter: self,
                formControlDefinition: formCtrlDef,
                oldValue: _oldValue,
                newValue: newVal,
            });
        }
        _oldValue = newVal;
    }

    function _fireInvalidChanged(silent) {
        //Get the new Invalid status
        var newVal = self.isInvalid();
        if (!silent) {
            self.emit("Invalid", {
                name: formCtrlDef.name,
                containingElem: self.arrayParentElem,
                controlAdapter: self,
                formControlDefinition: formCtrlDef,
                oldValue: _oldInvalidStatus,
                newValue: newVal,
            });
        }
        _oldInvalidStatus = newVal;
    }

    function _fireInProgressStatusChanged(silent) {
        let newInProgressStatus = self.isInProgress();
        if (!silent) {
            self.emit("InProgress", {
                name: formCtrlDef.name,
                containingElem: self.arrayParentElem,
                controlAdapter: self,
                formControlDefinition: formCtrlDef,
                oldInProgressStatus: _oldInProgressStatus,
                newInProgressStatus: newInProgressStatus,
            });
        }
        _oldInProgressStatus = newInProgressStatus;
    }

    /**
     * Set the initial state of the readOnly.
     * @private
     */
    function _setInitialReadOnlyState() {
        if (self._readOnlyField) {
            self.setReadOnly(true);
        }
        if (self._multiEditMode) {
            self.setReadOnly(true);
        }
    }

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;
    if (this.name === "@Complete") {
        this.templateName = "templatecache/templates/array@Complete.template.html";
    } else {
        this.templateName = "templatecache/templates/array.template.html";
    }
    this.arrayParentElem = {};

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        let val = [];
        _items.forEach(function (item, index) {
            if (item.renderer || item.control) {
                let foundVal;
                if (item.control) {
                    foundVal = item.control.getValue();
                } else {
                    foundVal = item.renderer.api.getValue();
                }
                // If it's a string list handle it one way.
                if (_typeConfigSettings === "" || _.isNumber(_typeConfigSettings)) {
                    if (_isCheckboxList) {
                        if (foundVal) {
                            val.push(item.renderer.settings.name);
                        }
                    } else {
                        if (!_.isEmpty(foundVal) || _.isNumber(foundVal)) {
                            if (_labels) {
                                let foundLabel = false;
                                _labels.forEach((label) => {
                                    if (foundVal === label.label) {
                                        val.push(label.value);
                                        foundLabel = true;
                                    }
                                });
                                if (!foundLabel) {
                                    val.push(foundVal);
                                }
                            } else {
                                val.push(foundVal);
                            }
                        }
                    }
                } else {
                    // Handle objects in the array.
                    if (!_.isEmpty(foundVal)) {
                        val.push(foundVal);
                    }
                }
            }
        });
        return val;
    };

    /**
     * Returns if the number control value is valid based on given validation config (min, max, onlyInteger, step)
     * @returns {boolean}
     */
    this.isInvalid = function () {
        return _items.some(function (item) {
            if (item.control) {
                return item.control.isInvalid && item.control.isInvalid();
            } else {
                return (
                    item.renderer &&
                    _.isFunction(item.renderer.api.isInvalid) &&
                    item.renderer.api.isInvalid()
                );
            }
        });
    };

    /**
     * Returns if any of the array control elements are in progress
     * @returns {boolean}
     */
    this.isInProgress = function () {
        return _items.some(function (item) {
            if (item.control) {
                return item.control.isInProgress && item.control.isInProgress();
            } else {
                return (
                    item.renderer &&
                    _.isFunction(item.renderer.api.isInProgress) &&
                    item.renderer.api.isInProgress()
                );
            }
        });
    };
    /**
     * Returns validation messages associated with underlying control based on given validation config (min, max, onlyInteger, step)
     * @returns {Array}
     */
    this.getValidationMessages = function () {
        let validationMessages = [];
        //below keeps track of if any of the array items have validation messages > 0
        let hasValidationMessages = false;
        _items.forEach(function (item) {
            let validationMessage = null;
            if (item.control) {
                validationMessage = _.get(item.control, "validationMessages", []);
            } else if (!_isCheckboxList) {
                validationMessage = _.isFunction(item.renderer.api.getValidationMessages)
                    ? item.renderer.api.getValidationMessages()
                    : [];
            }
            if (!hasValidationMessages && !_.isEmpty(validationMessage)) {
                hasValidationMessages = true;
            }
            validationMessages.push(validationMessage);
        });
        return hasValidationMessages ? validationMessages : [];
    };

    /**
     * Set up the labels for the array items.  This function currently
     * only operates on arrays of strings.
     * @private
     */
    this._initializeLabels = function () {
        // Only initialize labels for string values
        if (_typeConfigSettings === "" || _.isNumber(_typeConfigSettings)) {
            let display = _scope.formCtrlDef.configSettings.Display;
            let label;
            if (_isCheckboxList) {
                if (_.has(display, "checkList.labels")) {
                    _labels = [];
                    label = display.checkList.labels;
                }
            } else {
                if (_.has(display, "list.labels")) {
                    _labels = [];
                    label = display.list.labels;
                }
            }

            _scope.formCtrlDef.configSettings.value.forEach((dfltVal, index) => {
                if (label) {
                    let labelInfo = {
                        label: label[index],
                        value: dfltVal.value,
                    };
                    _labels.push(labelInfo);
                }
                if (_isCheckboxList) {
                    this._createLabeledSimpleItem(dfltVal, index);
                }
            });
        }
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (value) {
        self.tearDown();
        if (this._accordionDisplay) {
            this.accordionIcon.addEventListener(
                "click",
                (this.toggleAccordion = this.toggleAccordion.bind(this))
            );
        }
        this._initializeLabels();
        if (value) {
            value.forEach((val, index) => {
                if (_isCheckboxList) {
                    _items.forEach(function (item, index) {
                        if (item.renderer.settings.name === val) {
                            item.control.setValue(true);
                        }
                    });
                } else {
                    if (_typeConfigSettings === "" || _.isNumber(_typeConfigSettings)) {
                        let newVal = {
                            value: val,
                        };
                        this._createLabeledSimpleItem(newVal, index);
                    } else {
                        _scope.Array.addItem(val);
                    }
                }
            });
        }
        return self.getValue();
    };

    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        addedItemPromises = [];
        if (this.accordionIcon) {
            this.accordionIcon.removeEventListener("click", this.toggleAccordion);
        }
        Object.keys(_itemsRegistry).forEach(function (itemId) {
            _scope.Array.closeItem(itemId, true);
        });
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @param $templateCache {$templateCache}
     * @param $timeout
     * @param $compile
     */
    this.$init = function ($scope, $templateCache, $timeout, $compile) {
        _scope = $scope;
        $scope.formCtrlDef = formCtrlDef;

        var arrayConfigSettings = formCtrlDef.configSettings.value;

        if (formCtrlDef.configSettings.Display) {
            let display = formCtrlDef.configSettings.Display;
            let listOptions;
            if (display.list) {
                listOptions = display.list;
            } else if (display.checkList) {
                _isCheckboxList = true;
                _allowAdd = false;
                _allowClone = false;
                _allowCheckAll = true;
                _allowUncheckAll = true;
                _allowDelete = false;

                listOptions = display.checkList;
            }
            if (listOptions) {
                if (!_.isUndefined(listOptions.showDelete)) {
                    _allowDelete = listOptions.showDelete;
                }
                if (!_.isUndefined(listOptions.showAdd)) {
                    _allowAdd = listOptions.showAdd;
                }
                if (!_.isUndefined(listOptions.showClone)) {
                    _allowClone = true;
                }
                if (!_.isUndefined(listOptions.showReOrder)) {
                    _allowReOrder = listOptions.showReOrder;
                }
                // Handle second case of showReorder spelling
                if (!_.isUndefined(listOptions.showReorder)) {
                    _allowReOrder = listOptions.showReorder;
                }
                if (!_.isUndefined(listOptions.allowEdit)) {
                    _allowEdit = listOptions.allowEdit;
                }
                if (!_.isUndefined(listOptions.showCheckAll)) {
                    _allowCheckAll = listOptions.showCheckAll;
                }
                if (!_.isUndefined(listOptions.showUnCheckAll)) {
                    _allowUncheckAll = listOptions.showUnCheckAll;
                }
                if (!_.isUndefined(listOptions.minLineCount)) {
                    _displayLineCount = listOptions.minLineCount;
                }
            }
        }
        if (_.isEmpty(arrayConfigSettings) || arrayConfigSettings[0].fieldType === "String") {
            _typeConfigSettings = "";
        } else if (arrayConfigSettings[0].fieldType === "Number") {
            _typeConfigSettings = 0;
        } else {
            _typeConfigSettings = arrayConfigSettings[0].value;
        }

        //Setup Settings Object
        $scope.Array = {
            _itemsRegistry: _itemsRegistry,
            _defaultSimpleValue: _defaultSimpleValue,
            moveItemDown: function (itemId) {
                var item = _itemsRegistry[itemId];
                if (item) {
                    var index = _items.indexOf(item);
                    if (index !== -1 && index !== _items.length) {
                        var destIndex = index + 1;
                        _items.splice(destIndex, 0, _items.splice(index, 1)[0]);
                        //move Dom Elem
                        item.renderer.elem.insertAfter(item.renderer.elem.next());
                    }
                }
                _fireValueChanged();
            },
            moveItemUp: function (itemId) {
                var item = _itemsRegistry[itemId];
                if (item) {
                    var index = _items.indexOf(item);
                    if (index !== -1 && index !== 0) {
                        var destIndex = index - 1;
                        _items.splice(destIndex, 0, _items.splice(index, 1)[0]);
                        //move Dom Elem
                        item.renderer.elem.insertBefore(item.renderer.elem.prev());
                    }
                }
                _fireValueChanged();
            },
            closeItem: function (itemId, silent) {
                var item = _itemsRegistry[itemId];
                if (_.has(item, "control")) {
                    item.control.removeListener("Value", self._onControlValueChange);
                    item.control.shutdown();
                } else {
                    item.renderer.api.tearDown();
                }
                item.renderer.elem.remove();
                delete _itemsRegistry[itemId];
                var index = _items.indexOf(item);
                if (index !== -1) {
                    _items.splice(index, 1);
                }
                if (!silent) {
                    _fireValueChanged();
                }
            },
            cloneItem: function (itemId) {
                if (itemId) {
                    let item = _itemsRegistry[itemId];
                    let index = _items.indexOf(item);
                    $scope.Array.addItem(formCtrlDef.value[index]);
                }
                _fireValueChanged();
            },
            checkAll: function () {
                if (_typeConfigSettings === "" || _.isNumber(_typeConfigSettings)) {
                    _items.forEach(function (item) {
                        if (item.control) {
                            item.control.setValue(true);
                        }
                    });
                }
                _fireValueChanged();
            },
            uncheckAll: function () {
                if (_typeConfigSettings === "" || _.isNumber(_typeConfigSettings)) {
                    _items.forEach(function (item) {
                        if (item.control) {
                            item.control.setValue(false);
                        }
                    });
                }
                _fireValueChanged();
            },
            /**
             * Append a new Array Item to the end of the array
             * @param configData {ConfigurationData} The configData to populate this item with.
             */
            addItem: function (configData) {
                if (_typeConfigSettings === "" || _.isNumber(_typeConfigSettings)) {
                    $scope.Array._addSimpleItem(configData);
                } else {
                    $scope.Array._addObjectItem(configData);
                }
            },
            /**
             * Append a new Array String Item to the end of the array
             * @param configData {ConfigurationData} The configData to populate this item with.
             */
            _addSimpleItem: function (newValue) {
                let configData = newValue;
                if (_.isUndefined(configData)) {
                    configData = {
                        name: _defaultSimpleValue,
                        value: _defaultSimpleValue,
                        label: _defaultSimpleValue,
                    };
                }

                let id = Uuid();

                let item = {id: id, renderer: {}};
                if (_isCheckboxList) {
                    let checkboxOptions = {
                        labelShown: true,
                        label: configData.label,
                        title: configData.title,
                    };
                    item.control = new CheckBox(checkboxOptions);
                    item.control.on(
                        "Value",
                        (self._onControlValueChange = self._onControlValueChange.bind(self))
                    );
                    item.control.on(
                        "Invalid",
                        (self._onControlInvalidChange = self._onControlInvalidChange.bind(self))
                    );
                    item.renderer = {settings: {name: configData.name}};
                } else if (_.isNumber(_typeConfigSettings) || _typeConfigSettings === "") {
                    let controlConfig = {
                        value: configData.label,
                        name: configData.name,
                        label: configData.label,
                    };
                    item.control = _.isNumber(_typeConfigSettings)
                        ? new InputNumberControl(controlConfig)
                        : new InputTextControl(controlConfig);
                    item.control.on(
                        "Value",
                        (self._onControlValueChange = self._onControlValueChange.bind(self))
                    );
                    item.control.on(
                        "Invalid",
                        (self._onControlInvalidChange = self._onControlInvalidChange.bind(self))
                    );
                } else {
                    item.renderer = {
                        api: {
                            tearDown: function () {},
                        },
                        settings: {
                            value: configData.label,
                            name: configData.name,
                            label: configData.label,
                            onUserChange: function (newText, oldText) {
                                _fireValueChanged();
                            },
                        },
                    };
                }
                _itemsRegistry[item.id] = item;
                _items.push(item);
                //Render
                let itemElem;
                if (_isCheckboxList) {
                    itemElem =
                        '<div class="array_item_renderer array_string_item_renderer array_checkbox">';
                } else {
                    itemElem = '<div class="array_item_renderer array_string_item_renderer">';
                }
                if (_allowReOrder || _allowDelete) {
                    itemElem += '<div class="array_item_actions">';
                    if (_allowDelete) {
                        itemElem +=
                            '<i class="fa fa-times close_item" data-ng-click="Array.closeItem(\'' +
                            item.id +
                            "')\"></i>";
                    }
                    if (_allowReOrder) {
                        itemElem +=
                            '<i class="fa fa-arrow-up move_item_up" data-ng-click="Array.moveItemUp(\'' +
                            item.id +
                            "')\"></i>";
                        itemElem +=
                            '<i class="fa fa-arrow-down move_item_down" data-ng-click="Array.moveItemDown(\'' +
                            item.id +
                            "')\"></i>";
                    }
                    if (_allowClone) {
                        itemElem +=
                            '<i class="fa fa-clone move_item_down" title="Clone" data-ng-click="Array.cloneItem(\'' +
                            item.id +
                            "')\"></i>";
                    }
                    itemElem += "</div>";
                }

                var itemHeight;
                if (!item.control) {
                    itemElem +=
                        "<gl-textfield " +
                        "api=\"Array._itemsRegistry['" +
                        item.id +
                        "'].renderer.api\" " +
                        "settings=\"Array._itemsRegistry['" +
                        item.id +
                        "'].renderer.settings\"></gl-textfield>";
                    itemHeight = 35;
                }
                itemElem += "</div>";

                var textFieldElem = $(itemElem);
                if (item.control) {
                    itemHeight = 43;
                    textFieldElem.append(item.control.renderer.rootElement);
                }

                // Create a link function by compiling the template
                var linkFn = $compile(textFieldElem);
                // Create the element with the compiled template with the same sc//ope
                var elem = linkFn($scope);
                // Append the element to the DOM
                var arrayItemsContainerElem = self.arrayParentElem.find(
                    "> .array_nested_container > .array_container"
                );
                arrayItemsContainerElem.addClass("array_list_container");
                arrayItemsContainerElem.append(elem);

                if (_displayLineCount) {
                    arrayItemsContainerElem.height(_displayLineCount * itemHeight);
                }
                item.renderer.elem = elem;

                if (!loadedDeferred.promise.isFulfilled()) {
                    var stringItemDeffered = q.defer();
                    addedItemPromises.push(stringItemDeffered.promise);
                    $timeout(function () {
                        stringItemDeffered.resolve();
                    });
                }
                $timeout(function () {
                    _fireValueChanged();
                });
            },
            /**
             * Append a new Array Object Item to the end of the array
             * @param configData {ConfigurationData} The configData to populate this item with.
             */
            _addObjectItem: function (configData) {
                var item = {
                    id: Uuid(),
                    renderer: {
                        api: {},
                        settings: {
                            configurationSettings: _typeConfigSettings,
                            configurationData: configData,
                            refInstances: formCtrlDef.refInstances,
                            refTypes: formCtrlDef.refTypes,
                        },
                    },
                };
                _itemsRegistry[item.id] = item;
                _items.push(item);

                let styles = StyleUtil.getCssStyleString(
                    _.get(formCtrlDef, "configSettings.value[0].Display.styles.cssStyle", {})
                );
                let cssClassString =
                    "array_item_renderer array_object_item_renderer " +
                    StyleUtil.getCssClassString(
                        _.get(formCtrlDef, "configSettings.value[0].Display.styles.cssClass", [])
                    );

                //Render
                let itemElem =
                    "<gl-form-renderer " +
                    'class="' +
                    cssClassString +
                    '" ' +
                    'style="' +
                    styles +
                    '" ' +
                    "data-api=\"Array._itemsRegistry['" +
                    item.id +
                    "'].renderer.api\" " +
                    "data-settings=\"Array._itemsRegistry['" +
                    item.id +
                    "'].renderer.settings\">" +
                    '<div class="array_item_actions">';

                if (_allowReOrder || _allowDelete || _allowClone) {
                    if (_allowDelete) {
                        itemElem +=
                            '<i class="fa fa-times close_item" data-ng-click="Array.closeItem(\'' +
                            item.id +
                            "')\"></i>";
                    }
                    if (_allowReOrder) {
                        itemElem +=
                            '<i class="fa fa-arrow-up move_item_up" data-ng-click="Array.moveItemUp(\'' +
                            item.id +
                            "')\"></i>";
                        itemElem +=
                            '<i class="fa fa-arrow-down move_item_down" data-ng-click="Array.moveItemDown(\'' +
                            item.id +
                            "')\"></i>";
                    }
                    if (_allowClone) {
                        itemElem +=
                            '<i class="fa fa-clone move_item_down" title="Clone" data-ng-click="Array.cloneItem(\'' +
                            item.id +
                            "')\"></i>";
                    }
                }
                itemElem += "</div>" + "</gl-form-renderer>";

                var arrayItemRendererElem = $(itemElem);

                // Create a link function by compiling the template
                var linkFn = $compile(arrayItemRendererElem);
                // Create the element with the compiled template with the same scope
                var elem = linkFn($scope);
                // Append the element to the DOM
                var arrayItemsContainerElem = self.arrayParentElem.find(
                    "> .array_nested_container > .array_container"
                );
                arrayItemsContainerElem.append(elem);

                item.renderer.elem = elem;

                if (!loadedDeferred.promise.isFulfilled()) {
                    var objectItemDeffered = q.defer();
                    addedItemPromises.push(objectItemDeffered.promise);
                    //When all the nested controls are loaded
                    item.renderer.api.once("AllControlsLoaded", function (evt) {
                        objectItemDeffered.resolve();
                    });
                }

                //When the option being rendered's API is available
                item.renderer.api.on("AllControlsInitialized", function (evt) {
                    _fireValueChanged();
                });

                //When the option's value changes
                item.renderer.api.on("ValueChanged", function (evt) {
                    _fireValueChanged();
                });

                //When the option's invalid status changes
                item.renderer.api.on("Invalid", function (evt) {
                    _fireInvalidChanged();
                });

                //When the option's In Progress status changes
                item.renderer.api.on("InProgress", function (evt) {
                    _fireInProgressStatusChanged();
                });
            },
        };

        //Setup directive
        var template = $templateCache.get(this.templateName);
        this.arrayElem = $(template);

        if (this._accordionDisplay) {
            let rootElement = this._multiEditMode
                ? containingElem[0].parentElement
                : containingElem[0];
            let label = rootElement.children[0];
            this.accordionIcon = document.createElement("actionable-icon-gl");
            this.accordionIcon.classList.add("accordion_icon", "expanded");
            this.accordionIcon.dataset.dark = true;
            this.accordionIcon.dataset.icon = "form.expand_collapse";
            this.accordionIcon.addEventListener(
                "click",
                (this.toggleAccordion = this.toggleAccordion.bind(this))
            );
            label.prepend(this.accordionIcon);
            if (_.get(this._accordionDisplay, "default") === "collapsed") {
                this.toggleAccordion();
            }
        }

        let addArrayFooter = false;
        let arrayFooterElem = '<div class="array_footer">';
        if (_allowAdd) {
            arrayFooterElem +=
                '<a class="array-add-item array-footer-item" data-ng-click="Array.addItem()">Add Item +</a>';
            addArrayFooter = true;
        }
        if (_allowCheckAll) {
            arrayFooterElem +=
                '<a class="array-checkbox-select-all array-footer-item" data-ng-click="Array.checkAll()">Check All</a>';
            addArrayFooter = true;
        }
        if (_allowUncheckAll) {
            arrayFooterElem +=
                '<a class="array-checkbox-deselect-all array-footer-item" data-ng-click="Array.uncheckAll()">Uncheck All</a>';
            addArrayFooter = true;
        }
        arrayFooterElem += "</div>";
        if (addArrayFooter) {
            let arrayFooter = $(arrayFooterElem);
            this.arrayElem.append(arrayFooter);
        }
        this._attachToParentElement(containingElem, this.arrayElem);

        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        let arrayContainerElem = this.arrayElem.find(".array_container");
        StyleUtil.appendClassListToElement(
            _.get($scope, "jTypeContext.layout.container.cssClasses", []),
            arrayContainerElem[0]
        );

        // Apply item layout styles - these styles customize how this array element is placed in parent layout
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });

        return loadedDeferred.promise;
    };
    this.$init.$inject = ["$scope", "$templateCache", "$timeout", "$compile"];

    /**
     * This function allows derived objects to override this function.
     * @param containingElem
     * @param childElem
     * @private
     */
    this._attachToParentElement = function (containingElem, childElem) {
        containingElem.append(childElem);
        this.arrayParentElem = containingElem;
    };

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     * @param $timeout {$timeout} The Angular $timeout variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope, $timeout) {
        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
        }
        if (formCtrlDef.configData && !_.isEmpty(formCtrlDef.configData)) {
            //Update used/available based on configData
            self.setValue(formCtrlDef.configData);
            $timeout(function () {
                function finished() {
                    _setInitialReadOnlyState();
                    loadedDeferred.resolve();
                }

                q.all(addedItemPromises).done(finished, finished);
            });
        } else {
            //set the defaults...
            if ($scope.formCtrlDef.configSettings.value.length >= 1) {
                this._initializeLabels();
                $scope.formCtrlDef.configSettings.value.forEach((dfltVal, index) => {
                    if (_typeConfigSettings === "" || _.isNumber(_typeConfigSettings)) {
                        if (!_isCheckboxList) {
                            this._createLabeledSimpleItem(dfltVal, index);
                        }
                    } else if (dfltVal.hasOwnProperty("value")) {
                        //                       Ignore value.  No object should be created
                    } else {
                        $scope.Array.addItem(dfltVal);
                    }
                });
            }
            _setInitialReadOnlyState();
            loadedDeferred.resolve();
        }
        if (formCtrlDef.configSettings.Display && formCtrlDef.configSettings.Display.addLabel) {
            var addItemElem = this.arrayParentElem.find(
                "> .array_nested_container > .array_footer > .array-add-item"
            );
            if (addItemElem) {
                var newText = formCtrlDef.configSettings.Display.addLabel;
                if (newText.charAt(newText.length - 1) !== "+") {
                    newText += " +";
                }
                addItemElem.text(newText);
            }
        }
    };
    this.$compiledAndLinked.$inject = ["$scope", "$timeout"];

    this.setReadOnly = function (flag) {
        if (flag) {
            containingElem.addClass("read-only");
            containingElem.find(".gl-textfield-input").attr("readOnly", true).addClass("read-only");
        } else {
            containingElem.removeClass("read-only");
            containingElem
                .find(".gl-textfield-input")
                .attr("readOnly", false)
                .removeClass("read-only");
        }

        _items.forEach(function (item) {
            if (item.control) {
                item.control.setDisabled(flag);
            }
        });
        for (let id in _itemsRegistry) {
            let item = _itemsRegistry[id];
            if (_.has(item, "renderer.api.setReadOnly")) {
                item.renderer.api.setReadOnly(flag);
            }
        }
    };

    this.toggleAccordion = () => {
        let rootElement = this._multiEditMode ? containingElem[0].parentElement : containingElem[0];
        let collapsed = rootElement.classList.contains("collapsed");
        if (collapsed) {
            rootElement.classList.remove("collapsed");
        } else {
            rootElement.classList.add("collapsed");
        }
    };

    this._createLabeledSimpleItem = function (dfltVal, index) {
        let value = dfltVal.value;
        let label = value;
        let name = value;
        if (_isCheckboxList) {
            value = false;
        }
        if (_labels) {
            _labels.forEach((labelItem) => {
                if (labelItem.value === label) {
                    label = labelItem.label;
                }
            });
        }
        let item = {
            value: value,
            name: name,
            label: label,
        };
        _scope.Array.addItem(item);
    };

    this._onControlValueChange = function () {
        _fireValueChanged();
    };

    this._onControlInvalidChange = function () {
        _fireInvalidChanged();
    };

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
